/** @jsxImportSource @emotion/react */

import { ClipboardEvent } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled/macro'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined'
import { Box, IconButton } from '@mui/material'

import { AOSRJsonType, SecondaryActJsonType } from '../../Types/dataJSON'
import {
  IActTemplate,
  IFrontPageSection,
  IHeaderActSection,
  IInlineActSection,
  IPageSeparatorActSection,
  ISelectActSection,
  ISubHeaderActSection,
  ITableActSection,
  ITextActSection,
  ITextTemplateActSection,
  RowDataType,
  TableHeaderType,
} from '../template.type'

import { getRelativeSizePt, headingFontSize } from './basis'
import { EditableSpan } from './EditableSpan'
import { H1 } from './H1'
import { parseClipboardTable } from './parseClipboardTable'

type TableStyledType = {
  section: ITableActSection
}

const TableStyled = styled.table<TableStyledType>`
  border-collapse: collapse;
  width: ${props => props.section?.styles?.width};
  max-width: ${props => props.section?.styles?.width};
  margin: 0;
  margin-top: ${getRelativeSizePt(4)};
  table-layout: fixed;
  border: ${props =>
    props.section?.styles?.hasBorders === false ? 'none' : `1px solid black`};
`

type HeaderCellStyledType = {
  row: TableHeaderType
  align: 'center' | 'left' | 'right'
  hasBorders?: boolean
  isEditable?: boolean
  isSelected?: boolean
}

const HeaderCellStyled = styled.td<HeaderCellStyledType>`
  font-family: 'PT-Astra-Serif', serif;
  font-weight: bold;
  font-size: ${headingFontSize};
  border: ${props =>
    props.hasBorders === false
      ? 'none'
      : `${getRelativeSizePt(1)} solid black`};
  width: ${props => props.row.width};
  text-align: ${props => props.align};
  vertical-align: middle;
  padding: ${getRelativeSizePt(4)} ${getRelativeSizePt(0)};
  word-wrap: break-word;
  writing-mode: ${props => (props.row.vertical ? 'vertical-lr' : 'none')};
  transform: ${props => (props.row.vertical ? 'rotate(180deg)' : 'none')};
  &:hover {
    outline: ${props =>
      props.isEditable ? '3pt solid rgba(113, 201, 217, 0.5)' : 'none'};
  }
  &:active {
    outline: ${props => (props.isEditable ? '3pt solid #0666bf' : 'none')};
  }
  outline: ${props => (props.isSelected ? '3px solid #0088FF7F' : 'none')};

  background: linear-gradient(
    to bottom,
    ${props =>
      props.isEditable ? 'rgba(146, 241, 227, 0.21)' : 'rgb(255,255,255)'},
    ${props =>
        props.isEditable ? 'rgba(146, 241, 227, 0.21)' : 'rgb(255,255,255)'}
      ${getRelativeSizePt(20)}
  );

  border-bottom: ${props => (props.isEditable ? 'solid #000 2px' : 'none')};
`

type TableForActType = {
  actItems: AOSRJsonType | SecondaryActJsonType
  section: ITableActSection
  getValue: ({
    value,
    sectionTemplate,
    template,
  }: {
    value: string | RowDataType[][]
    sectionTemplate:
      | IHeaderActSection
      | IInlineActSection
      | ITextTemplateActSection
      | ITableActSection
      | IFrontPageSection
      | ISelectActSection
      | IPageSeparatorActSection
      | ITextActSection
      | ISubHeaderActSection
      | TableHeaderType
    template?: IActTemplate
  }) => string | Array<Array<RowDataType>>
  selectedField?: any
  onAddRowClick?: (key: string, newRow: RowDataType[]) => void
  onRemoveRowClick?: (key: string, rowIndex: number) => void
  onChange: (e: string, key: string, id: string) => void
  onToggle: (
    item: ITableActSection & {
      value: string
    },
  ) => void
}

export const TableForAct = ({
  actItems,
  section,
  getValue,
  selectedField,
  onAddRowClick,
  onRemoveRowClick,
  onChange,
  onToggle,
}: TableForActType) => {
  const createNewRow = () => {
    const firstRowHeader: TableHeaderType[] = section.headers[0]
    const colspanCount = firstRowHeader.reduce(
      (acc, cur) => acc + (cur.colspan ? cur.colspan - 1 : 0),
      0,
    )
    const columnsCount = firstRowHeader.length + colspanCount
    const RowCount =
      (Array.isArray(actItems[section.key]?.value) &&
        actItems[section.key]?.value.length) ||
      0

    const newRow: RowDataType[] = []

    for (let index = 0; index < columnsCount; index++) {
      newRow.push({ id: `${RowCount}_${index}`, text: '', editable: true })
    }

    return newRow
  }

  const tableData = getValue({
    value: actItems[section.key]?.value,
    sectionTemplate: section,
  })

  const isDataArray = Array.isArray(tableData)

  const handleAddRowClick = () => {
    onAddRowClick?.(section.key, createNewRow())
  }

  const getCellId = (index: number, cellIndex: number) => {
    return `${index}_${cellIndex}`
  }

  const handlePaste =
    (startRowIndex: number, startColumnIndex: number) =>
    (e: ClipboardEvent<HTMLSpanElement>) => {
      e.preventDefault()
      const clipboardData = parseClipboardTable(
        e.clipboardData.getData('text/html'),
        e.clipboardData.getData('text/plain'),
        tableData[startRowIndex]
          ? tableData[startRowIndex].length - startColumnIndex
          : undefined,
      )

      clipboardData.forEach((row, rowIndex) => {
        const targetRowIndex = startRowIndex + rowIndex
        const targetRow = tableData[targetRowIndex]

        if (targetRow) {
          row.forEach((column, columnIndex) => {
            const targetColumnIndex = startColumnIndex + columnIndex

            if (targetColumnIndex < targetRow.length) {
              onChange(
                column,
                section.key,
                getCellId(targetRowIndex, targetColumnIndex),
              )
            }
          })
        }
      })
    }

  return (
    <div
      key={section.title}
      css={css`
        width: ${section?.styles?.width};
        max-width: ${section?.styles?.width};
        position: relative;
        padding: 0;
        margin: ${getRelativeSizePt(8)} 0;
      `}
    >
      {section.title && (
        <H1
          css={css`
            margin-left: ${getRelativeSizePt(8)};
          `}
        >
          {section.title}
        </H1>
      )}

      <TableStyled section={section}>
        <thead>
          {section.headers.map(
            (headerRow: Array<TableHeaderType>, index: number) => (
              <tr key={index}>
                {headerRow.map((row: TableHeaderType, rowIndex: number) => (
                  <HeaderCellStyled
                    key={rowIndex + (row.key ?? row.text)}
                    align={rowIndex === 0 ? 'left' : 'center'}
                    colSpan={row.colspan}
                    hasBorders={section.styles?.hasBorders}
                    isEditable={
                      row.editable &&
                      section.dataType === 'EDITABLE_ASSIGNEES_TABLE'
                    }
                    isSelected={
                      row.key === selectedField &&
                      row.editable &&
                      section.dataType === 'EDITABLE_ASSIGNEES_TABLE'
                    }
                    row={row}
                    rowSpan={row.rowspan}
                    onClick={
                      section.dataType === 'EDITABLE_ASSIGNEES_TABLE'
                        ? /* @ts-expect-error row имеет больше полей чем аргумент onToggle. */
                          () => onToggle({ ...row })
                        : undefined
                    }
                  >
                    {row.type !== 'signature_form' && row.key
                      ? (getValue({
                          value: actItems[row.key]?.value,
                          sectionTemplate: row,
                        }) as Array<Array<RowDataType>>)
                      : row.text}
                  </HeaderCellStyled>
                ))}
              </tr>
            ),
          )}
        </thead>
        <tbody>
          {isDataArray &&
            tableData.map((row, index) => (
              <tr key={index}>
                {row?.map((cell, cellIndex) => (
                  <td
                    key={cell.id}
                    colSpan={cell.colspan}
                    css={css`
                      border: ${getRelativeSizePt(1)} solid black;
                      writing-mode: ${cell.vertical ? 'vertical-lr' : 'none'};
                      transform: ${cell.vertical ? 'rotate(180deg)' : 'none'};
                      text-align: center;
                      vertical-align: middle;
                      padding: 0;
                      margin: 0;
                      word-wrap: break-word;
                      ${section.editable && cellIndex === row.length - 1
                        ? 'overflow: visible; position: relative;'
                        : ''}
                    `}
                    rowSpan={cell.rowspan}
                  >
                    {section.editable ? (
                      <EditableSpan
                        contentEditable
                        isEditable
                        css={css`
                          display: inline-block;
                          width: 100%;
                          font-style: normal;
                          padding: ${cell.vertical
                            ? `0 ${getRelativeSizePt(4)} 0
                              ${getRelativeSizePt(4)} `
                            : `${getRelativeSizePt(4)} 0
                              ${getRelativeSizePt(4)} 0`};
                        `}
                        id={section.key + getCellId(index, cellIndex)}
                        isUnderlined={false}
                        role="textbox"
                        selectedId={selectedField}
                        suppressContentEditableWarning={true}
                        onPaste={handlePaste(index, cellIndex)}
                        onTextChange={value =>
                          onChange(
                            value ?? '',
                            section.key,
                            getCellId(index, cellIndex),
                          )
                        }
                        onToggle={value =>
                          onToggle({
                            ...section, // TODO не уверен
                            key: section.key + getCellId(index, cellIndex),
                            value: value ?? '',
                            title: '',
                            editable: section.editable,
                          })
                        }
                      >
                        {cell.text}
                      </EditableSpan>
                    ) : (
                      <div
                        css={css`
                          font-family: 'PT-Astra-Serif', serif;
                          font-size: ${headingFontSize};
                          display: inline-block;
                          width: 100%;
                          padding: ${cell.vertical
                            ? `0 ${getRelativeSizePt(4)} 0
                              ${getRelativeSizePt(4)} `
                            : `${getRelativeSizePt(4)} 0
                              ${getRelativeSizePt(4)} 0`};
                        `}
                      >
                        {cell.text}
                      </div>
                    )}
                    {section.editable && cellIndex === row.length - 1 && (
                      <IconButton
                        aria-label="Удалить строку"
                        color="error"
                        css={css`
                          position: absolute;
                          left: calc(100% + 4px);
                          top: 50%;
                          transform: translateY(-50%);
                        `}
                        size="small"
                        onClick={
                          onRemoveRowClick
                            ? () => onRemoveRowClick(section.key, index)
                            : undefined
                        }
                      >
                        <RemoveCircleOutlineOutlinedIcon
                          sx={{ zIndex: 1250 }}
                        />
                      </IconButton>
                    )}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </TableStyled>
      {section.editable && (
        <Box bottom={-35} position="absolute" right={0}>
          <IconButton
            aria-label="Добавить строку"
            color="primary"
            edge="end"
            onClick={handleAddRowClick}
          >
            <AddCircleOutlineOutlinedIcon
              sx={{
                zIndex: 1250,
              }}
            />
          </IconButton>
        </Box>
      )}
    </div>
  )
}
